import { getSafeForumRecoveryNext } from 'services/forumSessionRecovery';

type LoginLocation = {
  pathname?: string;
  search: string;
};

export type RedirectTarget = string | {
  pathname?: string;
  search?: string;
  hash?: string;
};

type LoginHistory = {
  location: {
    state?: {
      from?: RedirectTarget;
    };
  };
};

const BLOCKED_POST_LOGIN_PATHS = /^\/(?:login(?:\/|$)|logout(?:\/|$)|auth(?:\/|$)|api\/sso\/discourse(?:\/|$))/;
const CHAT_INVITE_PATH = /^\/chat\/invite\/[0-9a-f-]{36}\.[A-Za-z0-9_-]{43}(?:\?.*)?$/i;
const PENDING_CHAT_INVITE_KEY = 'ark.pending-chat-invite';
const PENDING_CHAT_INVITE_TTL_MS = 30 * 24 * 60 * 60 * 1000;

export function rememberPendingChatInvite(path: string): void {
  if (!CHAT_INVITE_PATH.test(path) || !isSafePostLoginRedirect(path)) return;
  try {
    window.localStorage.setItem(PENDING_CHAT_INVITE_KEY, JSON.stringify({
      path,
      expiresAt: Date.now() + PENDING_CHAT_INVITE_TTL_MS,
    }));
  } catch { /* storage may be disabled */ }
}

export function clearPendingChatInvite(): void {
  try { window.localStorage.removeItem(PENDING_CHAT_INVITE_KEY); } catch { /* storage may be disabled */ }
}

function pendingChatInvite(): string | undefined {
  try {
    const value = window.localStorage.getItem(PENDING_CHAT_INVITE_KEY);
    if (!value) return undefined;
    const pending = JSON.parse(value) as { path?: string; expiresAt?: number };
    if (typeof pending.path !== 'string' || !Number.isFinite(pending.expiresAt)
      || (pending.expiresAt as number) <= Date.now()
      || !CHAT_INVITE_PATH.test(pending.path) || !isSafePostLoginRedirect(pending.path)) {
      clearPendingChatInvite();
      return undefined;
    }
    return pending.path;
  } catch {
    clearPendingChatInvite();
    return undefined;
  }
}

export const toRedirectPath = (target?: RedirectTarget): string | undefined => {
  if (!target) return undefined;
  if (typeof target === 'string') return target;
  return `${target.pathname ?? ''}${target.search ?? ''}${target.hash ?? ''}` || undefined;
};

export function isSafePostLoginRedirect(redirect: string): boolean {
  try {
    const origin = window.location.origin;
    const parsed = new URL(redirect, origin);
    const isExplicitRelativePath = redirect.startsWith('/')
      && !redirect.startsWith('//')
      && !redirect.startsWith('/\\');
    const isSameOriginAbsoluteUrl = /^[a-z][a-z\d+.-]*:/i.test(redirect)
      && parsed.origin === origin;
    const isDiscourseSsoLogin = parsed.pathname === '/login'
      && parsed.searchParams.get('sso_flow') === 'true';

    return (isExplicitRelativePath || isSameOriginAbsoluteUrl)
      && parsed.origin === origin
      && (isDiscourseSsoLogin || !BLOCKED_POST_LOGIN_PATHS.test(parsed.pathname));
  } catch {
    return false;
  }
}

export function resolvePostLoginRedirect(
  location: LoginLocation,
  history: LoginHistory,
  defaultRedirect = '/profile'
): string {
  const stateRedirect = toRedirectPath(history.location.state?.from);
  if (stateRedirect && isSafePostLoginRedirect(stateRedirect)) {
    return stateRedirect;
  }

  const params = new URLSearchParams(location.search);
  if (params.get('sso_flow') === 'true') {
    return `/login${location.search}`;
  }

  const recoveryNext = getSafeForumRecoveryNext(location.search);
  if (recoveryNext && isSafePostLoginRedirect(recoveryNext)) {
    return recoveryNext;
  }

  const redeemCode = params.get('redeemCode');
  if (redeemCode) {
    return `/profile?redeemCode=${encodeURIComponent(redeemCode)}`;
  }

  const invite = pendingChatInvite();
  if (invite) return invite;

  return defaultRedirect;
}
